import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Select,
  Box,
  Image,
  Text,
} from "@chakra-ui/react";
import { updateDispute } from "api/orders";
import axios from "axios";

const UpdateDisputeAction = ({ row, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    note: row.note || "",
    resolved_date: row.resolved_date
      ? new Date(row.resolved_date).toISOString().split("T")[0]
      : "",
    proof_image: row.proof_image || "",
    status: row.status || "",
    proof_image_url: row.proof_image_url || "",
  });

  const getDisplayStatus = (status) => {
    if (status === "reviewed" || !status || status === "-") return "Pending";
    if (status === "resolved") return "Resolved";
    if (status === "lost") return "Lost";
    return status;
  };

  const getStatusOptions = (currentStatus) => {
    const options = [
      { value: "reviewed", label: "Pending" },
      { value: "resolved", label: "Resolved" },
      { value: "lost", label: "Lost" },
    ];
    return options;
  };

  const toast = useToast();

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    setFormData({
      note: row.note || "",
      resolved_date: row.resolved_date
        ? new Date(row.resolved_date).toISOString().split("T")[0]
        : "",
      proof_image: row.proof_image || "",
      status: row.status || "",
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      // Generate unique filename with timestamp
      const timestamp = new Date().getTime();
      const extension = file.name.split(".").pop();
      const uniqueFilename = `dispute_proof_${timestamp}.${extension}`;

      // Get presigned URL
      const token = localStorage.getItem("tokenKey");
      const presignedResponse = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/dashboard/storage/upload-url`,
        { filename: uniqueFilename },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      const { url } = presignedResponse.data.body;

      // Upload file to presigned URL

      await axios.put(
        url,
        new File([file], uniqueFilename, { type: file.type })
      );

      // Update form data with the file URL
      setFormData((prev) => ({
        ...prev,
        proof_image: uniqueFilename,
        proof_image_url: URL.createObjectURL(file),
      }));

      toast({
        title: "Image uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Error uploading image",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const submitData = {
        note: formData.note,
        proof_image: formData.proof_image,
      };
      const rowData = row.row;
      if (
        formData.status !== rowData.status &&
        formData.status !== "reviewed"
      ) {
        submitData.status = formData.status;
      }

      if (formData.status === "resolved") {
        submitData.resolved_date =
          formData.resolved_date || new Date().toISOString();
      }
      if (!submitData.status) submitData.status = rowData.status;
      const result = await updateDispute(rowData._id, submitData, toast);

      if (result) {
        toast({
          title: "Dispute updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        if (onUpdate) {
          onUpdate();
        }

        handleClose();
      }
    } catch (error) {
      toast({
        title: "Error updating dispute",
        description: error.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        colorScheme="blue"
        onClick={handleOpen}
        variant="outline"
      >
        Update
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Dispute - {row.order?.orderNumber}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={4}>
              <Text fontWeight="bold" mb={2}>
                Current Status: {getDisplayStatus(row.status)}
              </Text>
            </Box>

            <FormControl mb={4}>
              <FormLabel>Update Status</FormLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
              >
                {getStatusOptions(row.status).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            {formData.status !== "lost" && (
              <FormControl mb={4}>
                <FormLabel>Note</FormLabel>
                <Textarea
                  value={formData.note}
                  onChange={(e) => handleInputChange("note", e.target.value)}
                  placeholder="Enter note"
                  rows={4}
                />
              </FormControl>
            )}

            {formData.status === "resolved" && (
              <>
                <FormControl mb={4}>
                  <FormLabel>Proof Image</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImageUpload(file);
                      }
                    }}
                    isDisabled={uploadingImage}
                  />
                  {uploadingImage && (
                    <Text mt={2} color="blue.500">
                      Uploading image...
                    </Text>
                  )}
                  {formData.proof_image_url && formData.proof_image_url !== "" && (
                    <Box mt={2}>
                      <Text fontSize="sm" color="green.500">
                        Image uploaded successfully
                      </Text>
                      <Image
                        src={formData.proof_image_url}
                        alt="Proof"
                        maxW="200px"
                        maxH="200px"
                        mt={2}
                        border="1px solid #e2e8f0"
                        borderRadius="md"
                      />
                    </Box>
                  )}
                </FormControl>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Updating..."
            >
              Update Dispute
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateDisputeAction;
